import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';
import type { LeagueModel } from '@/models/LeagueModels';
import { resolveType } from '@/services/ServiceContainer';
import { LeagueService } from '@/services/LeagueService';

interface LeagueContextType {
    leagues: Record<string, LeagueModel>;
    loading: boolean;
    error: string | null;
    refreshLeagues: () => Promise<void>;
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

export const useLeague = () => {
    const context = useContext(LeagueContext);
    if (context === undefined) {
        throw new Error('useLeague must be used within a LeagueProvider');
    }
    return context;
};

export const LeagueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { appUser } = useUser();
    const [leagues, setLeagues] = useState<Record<string, LeagueModel>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadLeagueData = async (leagueId: string) => {
        try {
            const leagueService = resolveType(LeagueService);
            return await leagueService.get(leagueId);

        } catch (err) {
            console.error(`Error loading league ${leagueId}:`, err);
            return null;
        }
    };

    const loadAllLeagues = async () => {
        if (!appUser?.activeLeagues) {
            setLeagues({});
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const leagueIds = Object.keys(appUser.activeLeagues);
            const leaguePromises = leagueIds.map(loadLeagueData);
            const leagueResults = await Promise.all(leaguePromises);

            const loadedLeagues: Record<string, LeagueModel> = {};
            leagueResults.forEach((league, index) => {
                if (league) {
                    loadedLeagues[leagueIds[index]] = league;
                }
            });

            setLeagues(loadedLeagues);
        } catch (err) {
            console.error('Error loading leagues:', err);
            setError('Failed to load leagues');
        } finally {
            setLoading(false);
        }
    };

    // Load leagues when user's active leagues change
    useEffect(() => {
        loadAllLeagues();
    }, [appUser?.activeLeagues]);

    return (
        <LeagueContext.Provider
            value={{
                leagues,
                loading,
                error,
                refreshLeagues: loadAllLeagues
            }}
        >
            {children}
        </LeagueContext.Provider>
    );
}; 